import store from '../store/index';
import { Device } from 'framework7/framework7-lite.esm.bundle';
import { initializeApp } from 'firebase/app';
import {
  getAnalytics,
  setAnalyticsCollectionEnabled,
  logEvent,
  setUserId,
  setUserProperties,
} from 'firebase/analytics';

/**
 * @file FirebaseAnalytics.js
 * @description Capa intermedia que permite la abstracción del uso de librerías y dependencias para el consumo de
 * los servicios de Firebase Analytics de manera transparente y unificada, independientemente si la app está
 * compilada usando Cordova o si es una web app.
 *
 * - En **Cordova**, utiliza `cordova-plugin-firebasex`.
 * - En **Web**, usa el SDK oficial de Firebase.
 *
 * @see https://github.com/dpa99c/cordova-plugin-firebasex#analytics
 * @see https://firebase.google.com/docs/analytics
 */

/**
 * Configuración de Firebase para Web.
 * Se inicializa solo en entorno Web para evitar conflictos con Cordova.
 *
 * @constant {Object} firebaseConfig - Configuración específica del proyecto Firebase para Web.
 */
const firebaseConfig = require(`../../${process.env.FIREBASE_CONFIG}`).default;

// Inicializar Firebase solo si no está inicializado previamente y si no es Cordova.
const analytics = !Device.cordova ? getAnalytics(initializeApp(firebaseConfig)) : null;

/**
 * Obtiene el usuario desde Vuex.
 * @returns {object|null} El objeto de usuario o null si no hay usuario logueado.
 */
function getUserFromStore() {
  return store.state.authentication.user || null;
}

/**
 * Obtiene las propiedades del usuario.
 * @returns {object} Objeto con las propiedades del usuario.
 */
function getUserProperties() {
  const { user } = getUserFromStore();
  return {
    fullname: `${user.contact.name} ${user.contact.surname}`,
    email: user.email,
  };
}

/**
 * Obtiene las propiedades de la plantación.
 * @param {object} plantation Objeto que contiene la información de la plantación.
 * @returns {object} Objeto con las propiedades de la plantación.
 */
function getPlantationProperties(plantation) {
  if (!plantation) return {};
  return {
    company_name: plantation.company_name ?? plantation.company ?? '', // Se tiene en cuenta la estructura distinta que hay en ciertos puntos de la plataforma
    explotation_name: plantation.explotation_name ?? plantation.explotation ?? '', // Se tiene en cuenta la estructura distinta que hay en ciertos puntos de la plataforma
    parcel_name: plantation.parcel_name ?? plantation.parcel ?? '', // Se tiene en cuenta la estructura distinta que hay en ciertos puntos de la plataforma
    sector_name: plantation.sector_name ?? plantation.sector ?? '', // Se tiene en cuenta la estructura distinta que hay en ciertos puntos de la plataforma
    plantation_name: plantation.name ?? '',
    species: plantation.species ?? '',
    variety: plantation.variety ?? '',
  };
}

/**
 * @typedef {object} FirebaseAnalytics
 * @description Interfaz unificada para Firebase Analytics en Cordova y Web.
 * @property {function} setAnalyticsCollectionEnabled - Activa o desactiva la recopilación de datos de Analytics.
 * @property {function} isAnalyticsCollectionEnabled - Devuelve si la colección de Analytics está habilitada.
 * @property {function} logEvent - Registra un evento en Firebase Analytics.
 * @property {function} setScreenName - Define el nombre de la pantalla actual.
 * @property {function} setUserId - Asigna un identificador único al usuario.
 * @property {function} setUserProperty - Define una propiedad de usuario con un valor específico.
 */
export default {
  /**
   * Activa o desactiva la recopilación de datos de Analytics para esta app en este dispositivo.
   *
   * @param {boolean} enabled - Indica si se habilita o no la recopilación.
   * @example FirebaseAnalytics.setAnalyticsCollectionEnabled(true);
   * @see https://github.com/dpa99c/cordova-plugin-firebasex#setanalyticscollectionenabled
   */
  setAnalyticsCollectionEnabled(enabled) {
    if (Device.cordova) {
      FirebasePlugin.setAnalyticsCollectionEnabled(enabled);
    } else {
      setAnalyticsCollectionEnabled(analytics, enabled);
    }
  },

  /**
   * Devuelve si la colección de Analytics está habilitada para esta app en este dispositivo.
   *
   * - En Cordova, obtiene el valor directamente.
   * - En Web, siempre devolverá `true` ya que la recopilación automática no se puede deshabilitar dinámicamente.
   *
   * @returns {Promise<boolean>} Si la recopilación de datos está habilitada.
   * @example const enabled = await FirebaseAnalytics.isAnalyticsCollectionEnabled();
   * @see https://github.com/dpa99c/cordova-plugin-firebasex#isanalyticscollectionenabled
   */
  async isAnalyticsCollectionEnabled() {
    return new Promise((resolve) => {
      if (Device.cordova) {
        FirebasePlugin.isAnalyticsCollectionEnabled((enabled) => resolve(enabled));
      } else {
        resolve(true); // Firebase Web no proporciona un método para verificar esto.
      }
    });
  },

  /**
   * Registra un evento en Firebase Analytics.
   *
   * @param {string} eventName - Nombre del evento (máx. 40 caracteres, solo letras, números y guiones bajos).
   * @param {object} eventProperties - Objeto con propiedades del evento (claves y valores limitados en tamaño).
   * @example FirebaseAnalytics.logEvent('select_content', {content_type: 'page_view', item_id: 'home'});
   * @see https://firebase.google.com/docs/analytics/events
   * @see https://github.com/dpa99c/cordova-plugin-firebasex#logevent
   */
  logEvent(eventName, eventProperties, plantation = null) {
    const properties = { ...eventProperties, ...getUserProperties(), ...getPlantationProperties(plantation) };
    if (Device.cordova) {
      FirebasePlugin.logEvent(eventName, properties);
    } else {
      logEvent(analytics, eventName, properties);
    }
  },

  /**
   * Define el nombre de la pantalla actual para Analytics.
   *
   * @param {string} screenName - Nombre de la pantalla actual.
   * @param {object} eventProperties - Propiedades adicionales del evento de vista.
   * @example FirebaseAnalytics.setScreenName('login');
   * @see https://github.com/dpa99c/cordova-plugin-firebasex#setscreenname
   */
  setScreenName(screenName, eventProperties = {}) {
    const properties = { ...eventProperties, ...getUserProperties() };
    if (Device.cordova) {
      FirebasePlugin.setScreenName(screenName);
    } else {
      logEvent(analytics, 'screen_view', {
        firebase_screen: screenName,
        firebase_screen_class: screenName,
        ...properties,
      });
    }
  },

  /**
   * Asigna un identificador de usuario único en Firebase Analytics.
   *
   * @param {string} id - Identificador único del usuario.
   * @example FirebaseAnalytics.setUserId('123456789');
   * @see https://github.com/dpa99c/cordova-plugin-firebasex#setuserid
   */
  setUserId(id) {
    if (Device.cordova) {
      FirebasePlugin.setUserId(id);
    } else {
      setUserId(analytics, id);
    }
  },

  /**
   * Define una propiedad de usuario en Firebase Analytics.
   *
   * @param {string} name - Nombre de la propiedad.
   * @param {string|number|boolean} value - Valor asignado a la propiedad.
   * @example FirebaseAnalytics.setUserProperty('name', 'John Doe');
   * @see https://support.google.com/firebase/answer/6317486
   * @see https://github.com/dpa99c/cordova-plugin-firebasex#setuserproperty
   */
  setUserProperty(name, value) {
    if (Device.cordova) {
      FirebasePlugin.setUserProperty(name, value);
    } else {
      setUserProperties(analytics, { [name]: value });
    }
  },

  /**
   * Asigna el identificador de usuario a Crashlytics para trazabilidad.
   *
   * @param {string} userId - Identificador único del usuario.
   * @example FirebaseAnalytics.setCrashlyticsUserId('user-123');
   * @see https://github.com/dpa99c/cordova-plugin-firebasex#setcrashlyticsuserid
   */
  setCrashlyticsUserId(userId) {
    if (Device.cordova) {
      FirebasePlugin.setCrashlyticsUserId(userId);
    } else {
      console.log('[Crashlytics Web Stub] setCrashlyticsUserId:', userId);
    }
  },

  /**
   * Registra una excepción no fatal en Crashlytics.
   *
   * Se utiliza para reportar errores controlados o excepciones capturadas que no provocan un crash,
   * pero que deben ser visibles en el panel de Crashlytics para diagnóstico.
   *
   * @param {Error|string} error - Objeto de excepción o mensaje de error personalizado.
   * @example FirebaseAnalytics.recordCrashlyticsException(new Error('Error al guardar configuración'));
   * @see https://github.com/dpa99c/cordova-plugin-firebasex#logerror
   */
  recordCrashlyticsException(error) {
    if (Device.cordova) {
      const message = typeof error === 'string' ? error : error.message || 'Unknown error';
      FirebasePlugin.logError(message);
    } else {
      console.error('[Crashlytics Web Stub] recordCrashlyticsException:', error);
    }
  },

  /**
   * Registra un mensaje contextual en Crashlytics.
   *
   * Este log se añade a la traza interna de Crashlytics y puede ayudar a entender
   * el flujo de ejecución previo a una excepción o crash.
   *
   * @param {string} message - Mensaje personalizado que describa el contexto del error o del estado de la app.
   * @example FirebaseAnalytics.logCrashlyticsMessage('Inicio de sincronización con backend');
   * @see https://github.com/dpa99c/cordova-plugin-firebasex#logmessage
   */
  logCrashlyticsMessage(message) {
    if (Device.cordova) {
      FirebasePlugin.logMessage(message);
    } else {
      console.log('[Crashlytics Web Stub] logCrashlyticsMessage:', message);
    }
  },

  /**
   * Establece una clave personalizada en Crashlytics para aportar contexto adicional a los errores.
   *
   * Las claves personalizadas se adjuntan automáticamente a los reportes de errores y ayudan a identificar
   * el estado de la aplicación, tipo de usuario o cualquier otro dato relevante al momento del fallo.
   *
   * @param {string} key - Clave identificadora (por ejemplo, 'user_role', 'api_status').
   * @param {string|number|boolean} value - Valor asociado a la clave.
   * @example FirebaseAnalytics.setCrashlyticsCustomKey('sync_status', 'timeout');
   * @see https://github.com/dpa99c/cordova-plugin-firebasex#setcrashlyticscustomkey
   */
  setCrashlyticsCustomKey(key, value) {
    if (Device.cordova) {
      FirebasePlugin.setCrashlyticsCustomKey(key, value);
    } else {
      console.log(`[Crashlytics Web Stub] setCrashlyticsCustomKey: ${key} = ${value}`);
    }
  },
};
